import { useMemo, useState } from "react";
import { Clock, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function minutesToTime(total) {
  const h = String(Math.floor(total / 60)).padStart(2, "0");
  const m = String(total % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function generateSlots(startMin, endMin, stepMin) {
  const slots = [];
  for (let t = startMin; t <= endMin; t += stepMin) slots.push(minutesToTime(t));
  return slots;
}

// 15-minute tee time slots: AM 7:00–11:45, PM 12:00–14:30.
// Fallback grid only — used if a course somehow has no tee_slots saved yet.
// capacity: null means "unknown/unlimited" — never shown as full.
export const TEE_TIME_SLOTS = {
  AM: generateSlots(7 * 60, 11 * 60 + 45, 15).map((time) => ({ time, capacity: null })),
  PM: generateSlots(12 * 60, 14 * 60 + 30, 15).map((time) => ({ time, capacity: null })),
};

function timeToPeriod(time) {
  const hour = parseInt(time.split(":")[0], 10);
  return hour >= 12 ? "PM" : "AM";
}

/**
 * Builds the AM/PM grid to render from a course's real tee_slots (as saved
 * by CreateCourseAdmin: [{ time, enabled, capacity }]). Slots the admin
 * disabled for this course are excluded entirely — they were never offered.
 */
function buildSlotGrid(teeSlots) {
  const enabled = teeSlots
    .filter((s) => s.enabled)
    .slice()
    .sort((a, b) => a.time.localeCompare(b.time));
  const grid = { AM: [], PM: [] };
  for (const s of enabled) grid[timeToPeriod(s.time)].push(s);
  return grid;
}

/**
 * @param {{
 *   value: string | undefined,
 *   onChange: (time: string) => void,
 *   disabled?: boolean,
 *   label?: string,
 *   teeSlots?: Array<{ time: string, enabled: boolean, capacity?: number }>,
 *   blockedTimes?: string[],
 *   bookedCounts?: Record<string, number>,
 *   open?: boolean,
 *   onOpenChange?: (open: boolean) => void,
 * }} props
 */
export default function TeeTimePopover({ value, onChange, disabled, label = "Select tee time", teeSlots, blockedTimes = [], bookedCounts = {}, open: openProp, onOpenChange }) {
  // Uncontrolled by default; the parent can pass `open`/`onOpenChange` to
  // drive it externally (e.g. auto-open right after a course is picked).
  const [openState, setOpenState] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? onOpenChange : setOpenState;
  const [tab, setTab] = useState(value && parseInt(value.split(":")[0], 10) >= 12 ? "PM" : "AM");
  const AnyPopoverContent = /** @type {any} */ (PopoverContent);

  // Prefer the course's real, admin-configured slots; fall back to the
  // static full-day grid only if none were provided.
  const slotGrid = useMemo(
    () => (Array.isArray(teeSlots) && teeSlots.length > 0 ? buildSlotGrid(teeSlots) : TEE_TIME_SLOTS),
    [teeSlots],
  );
  const blockedSet = useMemo(() => new Set(blockedTimes), [blockedTimes]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#0B2421] rounded-lg px-3 py-2 text-sm transition-colors w-full sm:w-[200px] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Clock className="h-4 w-4 text-[#D4AF37] shrink-0" />
          <span className={value ? "text-[#0B2421] font-medium" : "text-gray-400"}>{value ? `${value} WIB` : label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-gray-400 ml-auto shrink-0" />
        </button>
      </PopoverTrigger>
      <AnyPopoverContent className="w-auto p-4 bg-white rounded-xl border border-gray-100 shadow-xl z-50" align="start">
        <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-2">Indonesia Time (WIB)</p>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden mb-3">
          {["AM", "PM"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-5 py-1.5 text-xs font-bold transition-colors ${
                tab === t ? "bg-[#0B2421] text-white" : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        {slotGrid[tab].length === 0 ? (
          <p className="text-xs text-gray-400 w-64 py-4 text-center">No {tab} slots available for this course.</p>
        ) : (
          <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto w-64">
            {slotGrid[tab].map((slot) => {
              const bookedCount = bookedCounts[slot.time] || 0;
              const isFull = slot.capacity != null && bookedCount >= slot.capacity;
              const isBlocked = blockedSet.has(slot.time);
              const isDisabled = isFull || isBlocked;
              const remaining = slot.capacity != null ? Math.max(0, slot.capacity - bookedCount) : null;
              const isLow = remaining != null && !isFull && remaining <= 5;

              let title;
              if (isFull) title = "Fully booked";
              else if (isBlocked) title = "Already booked for another round on this course";

              return (
                <button
                  key={slot.time}
                  type="button"
                  disabled={isDisabled}
                  title={title}
                  onClick={() => { onChange(slot.time); setOpen(false); }}
                  className={`h-11 rounded-lg border text-xs font-medium transition-colors flex flex-col items-center justify-center leading-tight ${
                    value === slot.time
                      ? "bg-[#0B2421] text-white border-[#0B2421]"
                      : isDisabled
                        ? "bg-gray-100 text-gray-300 border-gray-100 line-through cursor-not-allowed"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-[#0B2421] hover:text-[#0B2421]"
                  }`}
                >
                  <span>{slot.time}</span>
                  {isFull ? (
                    <span className="text-[9px] font-normal not-italic no-underline">Full</span>
                  ) : isLow ? (
                    <span className={`text-[9px] font-normal no-underline ${value === slot.time ? "text-white/70" : "text-amber-600"}`}>
                      {remaining} left
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </AnyPopoverContent>
    </Popover>
  );
}